import type { KeyboardEvent } from "react";
import type { EditorChange, EditorCommand, EditorSelection } from "./types";
import { editorIndent, listPrefixPattern } from "./constants";

export function normalizeSelection(selection: EditorSelection): EditorSelection {
  return {
    start: Math.min(selection.start, selection.end),
    end: Math.max(selection.start, selection.end),
  };
}

export function isUndoRedoShortcut(event: KeyboardEvent<HTMLTextAreaElement>) {
  // The textarea is a controlled input, so the browser's native undo/redo
  // stack drifts out of sync with React state. Route Ctrl/Cmd+Z to the custom
  // history instead: undo on plain, redo on Shift (or Ctrl/Cmd+Y).
  const isMod = event.ctrlKey || event.metaKey;

  if (!isMod) {
    return false;
  }

  if (event.key === "z" || event.key === "Z") {
    return true;
  }

  return event.key === "y" || event.key === "Y";
}

export function getFormattingShortcutCommand(
  event: KeyboardEvent<HTMLTextAreaElement>,
): EditorCommand | null {
  const isMod = event.ctrlKey || event.metaKey;

  if (!isMod || event.altKey) {
    return null;
  }

  const key = event.key.toLowerCase();

  if (key === "b" && !event.shiftKey) {
    return "bold";
  }

  if (key === "i" && !event.shiftKey) {
    return "italic";
  }

  if (key === "u" && !event.shiftKey) {
    return "underline";
  }

  if (key === "x" && event.shiftKey) {
    return "strikethrough";
  }

  if (key === "e" && !event.shiftKey) {
    return "inline-code";
  }

  if (key === "k" && !event.shiftKey) {
    return "link";
  }

  return null;
}

export function getTextareaSelection(textarea: HTMLTextAreaElement): EditorSelection {
  return {
    start: textarea.selectionStart,
    end: textarea.selectionEnd,
  };
}

export function getTextareaCursorLine(textarea: HTMLTextAreaElement) {
  const offset =
    textarea.selectionStart === textarea.selectionEnd ||
    textarea.selectionDirection === "backward"
      ? textarea.selectionStart
      : textarea.selectionEnd;

  return textarea.value
    .slice(0, Math.max(0, Math.min(offset, textarea.value.length)))
    .split("\n").length;
}

function lineNumberAtOffset(markdown: string, offset: number) {
  const clamped = Math.max(0, Math.min(offset, markdown.length));
  return markdown.slice(0, clamped).split("\n").length;
}

/**
 * 1-based inclusive visible-line range for active-line / gutter chrome.
 * Caret → single line. Selection → every line that contains selected text
 * (exclusive end on a newline does not pull in the following empty line).
 */
export function getHighlightedVisibleLineRange(
  markdown: string,
  selection: EditorSelection,
): { start: number; end: number } {
  const { start, end } = normalizeSelection(selection);

  if (start === end) {
    const line = lineNumberAtOffset(markdown, start);
    return { start: line, end: line };
  }

  const startLine = lineNumberAtOffset(markdown, start);
  const endOffset =
    markdown[end - 1] === "\n" ? Math.max(start, end - 1) : end;
  const endLine = lineNumberAtOffset(markdown, endOffset);

  return { start: startLine, end: Math.max(startLine, endLine) };
}

/**
 * Split a visible line into before / selected / after for overlay selection
 * chrome. Returns null when the selection does not intersect the line text
 * (caret, or selection only covers the trailing newline).
 */
export function sliceLineSelection(
  line: string,
  lineStart: number,
  selection: EditorSelection,
): { before: string; selected: string; after: string } | null {
  const { start, end } = normalizeSelection(selection);

  if (start === end) {
    return null;
  }

  const lineEnd = lineStart + line.length;
  const selectedStart = Math.max(start, lineStart);
  const selectedEnd = Math.min(end, lineEnd);

  if (selectedStart >= selectedEnd) {
    return null;
  }

  const localStart = selectedStart - lineStart;
  const localEnd = selectedEnd - lineStart;

  return {
    before: line.slice(0, localStart),
    selected: line.slice(localStart, localEnd),
    after: line.slice(localEnd),
  };
}

export function wrapSelectedMarkdown(
  markdown: string,
  selection: EditorSelection,
  before: string,
  after: string,
  placeholder: string,
): EditorChange {
  const { start, end } = normalizeSelection(selection);
  const selectedText = markdown.slice(start, end);

  // Toggle off when the selection itself carries the wrapping markers.
  if (
    selectedText.length >= before.length + after.length &&
    selectedText.startsWith(before) &&
    selectedText.endsWith(after)
  ) {
    const innerText = selectedText.slice(
      before.length,
      selectedText.length - after.length,
    );
    const innerStart = start + before.length;

    return {
      value: `${markdown.slice(0, start)}${innerText}${markdown.slice(end)}`,
      selection: {
        start: innerStart,
        end: innerStart + innerText.length,
      },
    };
  }

  // Toggle off when the markers sit just outside the selection — the state
  // left behind after a previous wrap selected the inner text. For single-char
  // markers (`*`, `` ` ``), guard against matching a run of the same character
  // (e.g. italic unwrapping a bold span).
  const isSingleCharMarker = before.length === 1 && before === after;
  const borderedBySame =
    isSingleCharMarker &&
    ((start - before.length - 1 >= 0 &&
      markdown[start - before.length - 1] === before) ||
      (end + after.length < markdown.length &&
        markdown[end + after.length] === after));

  if (
    !borderedBySame &&
    start >= before.length &&
    end + after.length <= markdown.length &&
    markdown.slice(start - before.length, start) === before &&
    markdown.slice(end, end + after.length) === after
  ) {
    const innerText = selectedText;
    const innerStart = start - before.length;

    return {
      value: `${markdown.slice(0, innerStart)}${innerText}${markdown.slice(end + after.length)}`,
      selection: {
        start: innerStart,
        end: innerStart + innerText.length,
      },
    };
  }

  const innerText = selectedText || placeholder;
  const replacement = `${before}${innerText}${after}`;
  const innerStart = start + before.length;

  return {
    value: `${markdown.slice(0, start)}${replacement}${markdown.slice(end)}`,
    selection: {
      start: innerStart,
      end: innerStart + innerText.length,
    },
  };
}

export function insertMarkdownLink(
  markdown: string,
  selection: EditorSelection,
): EditorChange {
  const { start, end } = normalizeSelection(selection);
  const selectedText = markdown.slice(start, end);

  // Toggle off when the selection itself is a complete markdown link.
  const exactLink = /^\[(.*)\]\(([^)]*)\)$/.exec(selectedText);

  if (exactLink) {
    const label = exactLink[1];

    return {
      value: `${markdown.slice(0, start)}${label}${markdown.slice(end)}`,
      selection: {
        start,
        end: start + label.length,
      },
    };
  }

  // Toggle off when the selection sits inside a markdown link (the state left
  // behind after a previous insert selected the label or the URL).
  const searchStart = Math.max(0, start - 512);
  const searchEnd = Math.min(markdown.length, end + 512);
  const around = markdown.slice(searchStart, searchEnd);
  const linkPattern = /\[([^\]]*)\]\(([^)]*)\)/g;
  let match: RegExpExecArray | null;

  while ((match = linkPattern.exec(around)) !== null) {
    const matchStart = searchStart + match.index;
    const matchEnd = matchStart + match[0].length;

    if (matchStart <= start && end <= matchEnd) {
      const label = match[1];

      return {
        value: `${markdown.slice(0, matchStart)}${label}${markdown.slice(matchEnd)}`,
        selection: {
          start: matchStart,
          end: matchStart + label.length,
        },
      };
    }
  }

  const label = selectedText || "link text";
  const url = "https://example.com";
  const replacement = `[${label}](${url})`;
  const selectionStart = selectedText
    ? start + label.length + 3
    : start + 1;
  const selectionEnd = selectedText
    ? selectionStart + url.length
    : selectionStart + label.length;

  return {
    value: `${markdown.slice(0, start)}${replacement}${markdown.slice(end)}`,
    selection: {
      start: selectionStart,
      end: selectionEnd,
    },
  };
}

/** Pandoc-style size attrs inserted with new images from the toolbar. */
const defaultImageSizeAttrs = "{width=80px height=80px}";
const markdownImagePattern =
  /!\[([^\]]*)\]\(([^)]*)\)(?:\s*\{[^}]*\})?/g;

export function insertMarkdownImage(
  markdown: string,
  selection: EditorSelection,
): EditorChange {
  const { start, end } = normalizeSelection(selection);
  const selectedText = markdown.slice(start, end);

  // Toggle off when the selection itself is a complete markdown image
  // (optional Pandoc-style `{width=… height=…}` size attrs included).
  const exactImage = /^!\[(.*)\]\(([^)]*)\)(?:\s*\{[^}]*\})?$/.exec(
    selectedText,
  );

  if (exactImage) {
    const alt = exactImage[1];

    return {
      value: `${markdown.slice(0, start)}${alt}${markdown.slice(end)}`,
      selection: {
        start,
        end: start + alt.length,
      },
    };
  }

  // Toggle off when the selection sits inside a markdown image.
  const searchStart = Math.max(0, start - 512);
  const searchEnd = Math.min(markdown.length, end + 512);
  const around = markdown.slice(searchStart, searchEnd);
  const imagePattern = new RegExp(
    markdownImagePattern.source,
    markdownImagePattern.flags,
  );
  let match: RegExpExecArray | null;

  while ((match = imagePattern.exec(around)) !== null) {
    const matchStart = searchStart + match.index;
    const matchEnd = matchStart + match[0].length;

    if (matchStart <= start && end <= matchEnd) {
      const alt = match[1];

      return {
        value: `${markdown.slice(0, matchStart)}${alt}${markdown.slice(matchEnd)}`,
        selection: {
          start: matchStart,
          end: matchStart + alt.length,
        },
      };
    }
  }

  const alt = selectedText || "alt text";
  const url = "/logo.svg";
  const replacement = `![${alt}](${url})${defaultImageSizeAttrs}`;
  const selectionStart = selectedText
    ? start + alt.length + 4
    : start + 2;
  const selectionEnd = selectedText
    ? selectionStart + url.length
    : selectionStart + alt.length;

  return {
    value: `${markdown.slice(0, start)}${replacement}${markdown.slice(end)}`,
    selection: {
      start: selectionStart,
      end: selectionEnd,
    },
  };
}

export function insertMarkdownBlock(
  markdown: string,
  selection: EditorSelection,
  block: string,
  selectedRange: EditorSelection = { start: 0, end: block.length },
): EditorChange {
  const { start, end } = normalizeSelection(selection);
  const before = markdown.slice(0, start);
  const after = markdown.slice(end);
  const prefix =
    before.length === 0 || before.endsWith("\n\n")
      ? ""
      : before.endsWith("\n")
        ? "\n"
        : "\n\n";
  const suffix =
    after.length === 0 || after.startsWith("\n\n")
      ? ""
      : after.startsWith("\n")
        ? "\n"
        : "\n\n";
  const blockStart = before.length + prefix.length;

  return {
    value: `${before}${prefix}${block}${suffix}${after}`,
    selection: {
      start: blockStart + selectedRange.start,
      end: blockStart + selectedRange.end,
    },
  };
}

export function getSelectedLineRange(
  markdown: string,
  selection: EditorSelection,
): EditorSelection {
  const { start, end } = normalizeSelection(selection);
  const lineStart = markdown.lastIndexOf("\n", Math.max(0, start - 1)) + 1;
  const adjustedEnd = end > start && markdown[end - 1] === "\n" ? end - 1 : end;
  const nextBreak = markdown.indexOf("\n", adjustedEnd);

  return {
    start: lineStart,
    end: nextBreak === -1 ? markdown.length : nextBreak,
  };
}

export function transformSelectedLines(
  markdown: string,
  selection: EditorSelection,
  transformLine: (line: string, index: number) => string,
): EditorChange {
  const range = getSelectedLineRange(markdown, selection);
  const selectedLines = markdown.slice(range.start, range.end);
  const transformedLines = selectedLines.split("\n").map(transformLine).join("\n");

  return {
    value: `${markdown.slice(0, range.start)}${transformedLines}${markdown.slice(range.end)}`,
    selection: {
      start: range.start,
      end: range.start + transformedLines.length,
    },
  };
}

export function splitIndent(line: string) {
  const match = /^(\s*)(.*)$/.exec(line);

  return {
    indent: match?.[1] ?? "",
    content: match?.[2] ?? line,
  };
}

export const fenceLinePattern = /^```/;

export function unwrapFencedCode(
  markdown: string,
  selection: EditorSelection,
  language?: string,
): EditorChange | null {
  const range = getSelectedLineRange(markdown, selection);
  const before = markdown.slice(0, range.start);
  const beforeTrimmed = before.endsWith("\n") ? before.slice(0, -1) : before;
  const beforeLineStart = beforeTrimmed.lastIndexOf("\n") + 1;
  const beforeLine = beforeTrimmed.slice(beforeLineStart);

  if (!fenceLinePattern.test(beforeLine)) {
    return null;
  }

  if (language !== undefined) {
    const openLanguage = beforeLine.slice(3).trim();

    if (openLanguage !== language) {
      return null;
    }
  }

  const after = markdown.slice(range.end);

  if (!after.startsWith("\n")) {
    return null;
  }

  const afterBody = after.slice(1);
  const afterLineEnd = afterBody.indexOf("\n");
  const afterLine =
    afterLineEnd === -1 ? afterBody : afterBody.slice(0, afterLineEnd);

  if (!fenceLinePattern.test(afterLine)) {
    return null;
  }

  const closingFenceStart = range.end + 1;
  const closingFenceEnd = closingFenceStart + afterLine.length;
  const removeEnd = closingFenceEnd + (afterLineEnd === -1 ? 0 : 1);
  const innerLength = range.end - range.start;
  const trailingRest = markdown.slice(removeEnd);
  let value = `${markdown.slice(0, beforeLineStart)}${markdown.slice(range.start, range.end + 1)}${trailingRest}`;

  // Nothing follows the closing fence — drop the dangling trailing newline
  // left over from the removed code-block separator.
  if (trailingRest.length === 0 && value.endsWith("\n")) {
    value = value.slice(0, -1);
  }

  return {
    value,
    selection: {
      start: beforeLineStart,
      end: beforeLineStart + innerLength,
    },
  };
}

export function unwrapMathBlock(
  markdown: string,
  selection: EditorSelection,
): EditorChange | null {
  const range = getSelectedLineRange(markdown, selection);
  const before = markdown.slice(0, range.start);
  const beforeTrimmed = before.endsWith("\n") ? before.slice(0, -1) : before;
  const beforeLineStart = beforeTrimmed.lastIndexOf("\n") + 1;
  const beforeLine = beforeTrimmed.slice(beforeLineStart);

  if (beforeLine.trim() !== "$$") {
    return null;
  }

  const after = markdown.slice(range.end);

  if (!after.startsWith("\n")) {
    return null;
  }

  const afterBody = after.slice(1);
  const afterLineEnd = afterBody.indexOf("\n");
  const afterLine =
    afterLineEnd === -1 ? afterBody : afterBody.slice(0, afterLineEnd);

  if (afterLine.trim() !== "$$") {
    return null;
  }

  const closingStart = range.end + 1;
  const closingEnd = closingStart + afterLine.length;
  const removeEnd = closingEnd + (afterLineEnd === -1 ? 0 : 1);
  const innerLength = range.end - range.start;
  const trailingRest = markdown.slice(removeEnd);
  let value = `${markdown.slice(0, beforeLineStart)}${markdown.slice(range.start, range.end + 1)}${trailingRest}`;

  if (trailingRest.length === 0 && value.endsWith("\n")) {
    value = value.slice(0, -1);
  }

  return {
    value,
    selection: {
      start: beforeLineStart,
      end: beforeLineStart + innerLength,
    },
  };
}

const headingCommandLevels: Partial<Record<EditorCommand, number>> = {
  heading: 2,
  "heading-1": 1,
  "heading-2": 2,
  "heading-3": 3,
  "heading-4": 4,
  "heading-5": 5,
  "heading-6": 6,
};

function applyHeadingLevel(
  markdown: string,
  selection: EditorSelection,
  level: number,
): EditorChange {
  return transformSelectedLines(markdown, selection, (line) => {
    const { indent, content } = splitIndent(line);
    const exactMatch = new RegExp(`^#{${level}}\\s+`).exec(content);

    if (exactMatch) {
      return `${indent}${content.slice(exactMatch[0].length)}`;
    }

    const headingMatch = /^#{1,6}\s+/.exec(content);

    if (headingMatch) {
      return `${indent}${"#".repeat(level)} ${content.slice(headingMatch[0].length)}`;
    }

    return `${indent}${"#".repeat(level)} ${content}`;
  });
}

export function applyEditorCommand(
  command: EditorCommand,
  markdown: string,
  selection: EditorSelection,
): EditorChange {
  if (command === "bold") {
    return wrapSelectedMarkdown(markdown, selection, "**", "**", "bold text");
  }

  if (command === "italic") {
    return wrapSelectedMarkdown(markdown, selection, "*", "*", "italic text");
  }

  if (command === "underline") {
    return wrapSelectedMarkdown(markdown, selection, "<u>", "</u>", "underlined text");
  }

  if (command === "strikethrough") {
    return wrapSelectedMarkdown(markdown, selection, "~~", "~~", "strikethrough text");
  }

  if (command === "highlight") {
    return wrapSelectedMarkdown(markdown, selection, "==", "==", "highlighted text");
  }

  if (command === "inline-code") {
    return wrapSelectedMarkdown(markdown, selection, "`", "`", "code");
  }

  if (command === "math-inline") {
    return wrapSelectedMarkdown(markdown, selection, "$", "$", "a^2");
  }

  if (command === "paragraph") {
    return transformSelectedLines(markdown, selection, (line) => {
      const { indent, content } = splitIndent(line);
      const headingMatch = /^#{1,6}\s+/.exec(content);

      if (headingMatch) {
        return `${indent}${content.slice(headingMatch[0].length)}`;
      }

      return line;
    });
  }

  const headingLevel = headingCommandLevels[command];

  if (headingLevel !== undefined) {
    return applyHeadingLevel(markdown, selection, headingLevel);
  }

  if (command === "link") {
    return insertMarkdownLink(markdown, selection);
  }

  if (command === "image") {
    return insertMarkdownImage(markdown, selection);
  }

  if (command === "blockquote") {
    return transformSelectedLines(markdown, selection, (line) => {
      const { indent, content } = splitIndent(line);
      const quoteMatch = /^>\s?/.exec(content);

      if (quoteMatch) {
        return `${indent}${content.slice(quoteMatch[0].length)}`;
      }

      return `${indent}> ${content}`;
    });
  }

  if (command === "fenced-code") {
    const unwrapped = unwrapFencedCode(markdown, selection);

    if (unwrapped) {
      return unwrapped;
    }

    const { start, end } = normalizeSelection(selection);
    const selectedText = markdown.slice(start, end);
    const code = selectedText || "code";
    const block = ["```", code, "```"].join("\n");

    return insertMarkdownBlock(markdown, selection, block, {
      start: 4,
      end: 4 + code.length,
    });
  }

  if (command === "mermaid") {
    const unwrapped = unwrapFencedCode(markdown, selection, "mermaid");

    if (unwrapped) {
      return unwrapped;
    }

    const { start, end } = normalizeSelection(selection);
    const selectedText = markdown.slice(start, end).trim();
    const diagram =
      selectedText ||
      ["flowchart LR", "  A[Start] --> B[End]"].join("\n");
    const block = ["```mermaid", diagram, "```"].join("\n");
    const contentStart = "```mermaid\n".length;

    return insertMarkdownBlock(markdown, selection, block, {
      start: contentStart,
      end: contentStart + diagram.length,
    });
  }

  if (command === "math-block") {
    const unwrapped = unwrapMathBlock(markdown, selection);

    if (unwrapped) {
      return unwrapped;
    }

    const { start, end } = normalizeSelection(selection);
    const selectedText = markdown.slice(start, end).trim();
    const expression = selectedText || "E = mc^2";
    const block = ["$$", expression, "$$"].join("\n");

    return insertMarkdownBlock(markdown, selection, block, {
      start: 3,
      end: 3 + expression.length,
    });
  }

  if (command === "horizontal-rule") {
    return insertMarkdownBlock(markdown, selection, "---", {
      start: 0,
      end: 3,
    });
  }

  if (command === "line-break") {
    const { start, end } = normalizeSelection(selection);
    const snippet = "<br>";
    const caret = start + snippet.length;

    return {
      value: `${markdown.slice(0, start)}${snippet}${markdown.slice(end)}`,
      selection: {
        start: caret,
        end: caret,
      },
    };
  }

  if (command === "unordered-list") {
    return transformSelectedLines(markdown, selection, (line) => {
      const { indent, content } = splitIndent(line);
      const stripped = content.replace(listPrefixPattern, "");
      const isChecklist = /^- \[[ xX]\]\s+/.test(content);

      if (/^[-*+]\s+/.test(content) && !isChecklist) {
        return `${indent}${stripped}`;
      }

      return `${indent}- ${stripped}`;
    });
  }

  if (command === "ordered-list") {
    return transformSelectedLines(markdown, selection, (line, index) => {
      const { indent, content } = splitIndent(line);
      const stripped = content.replace(listPrefixPattern, "");

      if (/^\d+\.\s+/.test(content)) {
        return `${indent}${stripped}`;
      }

      return `${indent}${index + 1}. ${stripped}`;
    });
  }

  if (command === "checklist") {
    return transformSelectedLines(markdown, selection, (line) => {
      const { indent, content } = splitIndent(line);
      const uncheckedMatch = /^- \[ \]\s+/.exec(content);

      if (uncheckedMatch) {
        return `${indent}- [x] ${content.slice(uncheckedMatch[0].length)}`;
      }

      const checkedMatch = /^- \[[xX]\]\s+/.exec(content);

      if (checkedMatch) {
        return `${indent}${content.slice(checkedMatch[0].length)}`;
      }

      const stripped = content.replace(listPrefixPattern, "");

      return `${indent}- [ ] ${stripped}`;
    });
  }

  return insertMarkdownBlock(
    markdown,
    selection,
    ["| Column | Value |", "| --- | --- |", "| Item | Ready |"].join("\n"),
  );
}

export function indentSelection(
  markdown: string,
  selection: EditorSelection,
): EditorChange {
  const { start, end } = normalizeSelection(selection);

  if (start === end) {
    return {
      value: `${markdown.slice(0, start)}${editorIndent}${markdown.slice(end)}`,
      selection: {
        start: start + editorIndent.length,
        end: start + editorIndent.length,
      },
    };
  }

  return transformSelectedLines(markdown, selection, (line) => `${editorIndent}${line}`);
}

export function outdentSelection(
  markdown: string,
  selection: EditorSelection,
): EditorChange {
  return transformSelectedLines(markdown, selection, (line) => {
    if (line.startsWith(editorIndent)) {
      return line.slice(editorIndent.length);
    }

    if (line.startsWith("\t")) {
      return line.slice(1);
    }

    return line;
  });
}
